import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { deleteObject, getStorage, ref } from 'firebase/storage';
import { db, functions } from './firebase';
import { uploadIncidentMedia } from '../components/Dashboard/Incidents/Media/mediaUploadService';

const configureAppointmentCallable = httpsCallable(functions, 'configureAppointment');
const createAppointmentFromCaptureCallable = httpsCallable(functions, 'createAppointmentFromCapture');
const updateAppointmentStatusCallable = httpsCallable(functions, 'updateAppointmentStatus');
const saveAppointmentTimeZoneCallable = httpsCallable(functions, 'saveAppointmentTimeZone');
const DEFINITIVE_REJECTION_CODES = new Set([
  'functions/invalid-argument', 'functions/permission-denied', 'functions/unauthenticated', 'functions/not-found',
]);
export const isDefinitiveAppointmentCaptureRejection = (error) => DEFINITIVE_REJECTION_CODES.has(error?.code);

export const configureAppointment = async (appointment) => {
  const result = await configureAppointmentCallable(appointment);
  return result.data;
};

export const saveAppointmentCapture = async ({ appointment, capture, deps = {} }) => {
  const uploadVoice = deps.uploadVoice || uploadIncidentMedia;
  const createFromCapture = deps.createFromCapture || createAppointmentFromCaptureCallable;
  const removeUpload = deps.removeUpload || ((path) => deleteObject(ref(getStorage(), path)));
  const clientRequestId = String(capture?.clientRequestId || '').trim();
  if (!clientRequestId) throw new Error('Appointment capture request ID is required');
  let uploadedAudio = null;
  if (capture?.inputMode === 'voice' && capture.audioBlob) {
    [uploadedAudio] = await uploadVoice(
      null,
      capture.audioBlob,
      `appointment-${clientRequestId}`,
      `careSources/voice/${appointment.childId}`,
      { audioObjectName: `appointment-${clientRequestId}` },
    );
  }
  const capturePayload = { ...(capture || {}) };
  delete capturePayload.audioBlob;
  try {
    const response = await createFromCapture({
      ...appointment,
      capture: {
        ...capturePayload,
        ...(uploadedAudio ? { audioAttachment: uploadedAudio } : {}),
      },
    });
    return response?.data || response;
  } catch (error) {
    if (uploadedAudio?.path && isDefinitiveAppointmentCaptureRejection(error)) {
      try { await removeUpload(uploadedAudio.path); } catch { /* cleanup is best effort */ }
    }
    throw error;
  }
};

export const updateAppointmentStatus = async (appointmentId, status) => {
  const result = await updateAppointmentStatusCallable({ appointmentId, status });
  return result.data;
};

export const saveAppointmentTimeZone = async (childId, timeZone) => {
  const result = await saveAppointmentTimeZoneCallable({ childId, timeZone });
  return result.data;
};

export const subscribeToAppointments = (childId, onChange, onError = () => {}) => {
  if (!childId) {
    onChange([]);
    return () => {};
  }
  const appointmentQuery = query(
    collection(db, 'schedules'),
    where('childId', '==', childId),
    where('scheduleType', '==', 'appointment'),
  );
  return onSnapshot(appointmentQuery, (snapshot) => {
    const appointments = snapshot.docs
      .map((item) => ({ id: item.id, ...item.data() }))
      .sort((left, right) => `${left.startDate || ''}T${left.startTime || ''}`.localeCompare(`${right.startDate || ''}T${right.startTime || ''}`));
    onChange(appointments);
  }, onError);
};
